import React, { useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import robotAnimation from '../../../assets/lottie/robot.json';
import inteligentAutomationBg from '../../../assets/images/servicepage/intelligent_bg.webp';
import TitleHeader from '../../atoms/TitleHeader/TitleHeader';
import Constants from '../../../app/utils/constants';
import colors from '../../theme/color';

const CARD_STYLES = [
  {
    border: '1px solid #744DFF',
    headingStyle: { color: '#744DFF' },
  },
  {
    border: '1px solid transparent',
    headingStyle: {
      background: 'linear-gradient(94.73deg, #EA892A 0%, #E35384 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    gradientBorder: true,
  },
] as const;

const IntelligentAutomationSection = () => {
  const intelligentAutomation = Constants.servicePage.intelligentAutomation;
  const titleBadgeBorderClassName = 'bg-service-title-badge';

  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [visible, setVisible] = useState<boolean[]>([]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    cardRefs.current.forEach((card, index) => {
      if (!card) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            // enter → animate in
            setVisible((prev) => {
              const updated = [...prev];
              updated[index] = true;
              return updated;
            });
          } else {
            // leave → reset animation
            setVisible((prev) => {
              const updated = [...prev];
              updated[index] = false;
              return updated;
            });
          }
        },
        {
          threshold: 0.3, // card must be visible
          rootMargin: '0px 0px -10% 0px', // better trigger timing
        }
      );

      observer.observe(card);
      observers.push(observer);
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  return (
    <section className="relative z-10 w-full overflow-hidden px-6 py-12 sm:px-10 sm:py-14 lg:px-16 lg:py-14 xl:px-24 xl:py-18 2xl:px-32 2xl:py-20 min-[2560px]:px-44 min-[2560px]:py-26">

      <div className="relative mx-auto grid max-w-[1920px] grid-cols-1 items-center gap-8 lg:grid-cols-[minmax(20rem,23rem)_minmax(0,1fr)] lg:gap-5 xl:grid-cols-[minmax(22rem,26rem)_minmax(0,1fr)] xl:gap-7 2xl:max-w-[2200px] 2xl:grid-cols-[minmax(29rem,33rem)_minmax(0,1fr)] 2xl:gap-7 min-[2560px]:max-w-[2500px] min-[2560px]:grid-cols-[minmax(38rem,44rem)_minmax(0,1fr)] min-[2560px]:gap-8">

        {/* LEFT COLUMN (unchanged) */}
        <div className="relative z-10 flex flex-col items-center pt-4 text-center lg:max-w-[23rem] lg:items-start lg:text-left xl:max-w-[26rem] xl:translate-x-6 2xl:max-w-[33rem] min-[2560px]:max-w-[44rem]">

  <div className="absolute top-1/2 left-[50%] md:left-[50%] min-[2560px]:left-[188%] -translate-x-1/2 -translate-y-1/2 min-[2560px]:-translate-x-[28%] w-[200%] sm:w-[200%] max-w-[1600px] min-[2560px]:max-w-[1800px] aspect-square pointer-events-none -z-10 flex items-center justify-center">
    <img
      src={inteligentAutomationBg}
      alt="Background"
      className="w-full h-full object-contain opacity-10"
      style={{ filter: 'grayscale(0.5) contrast(0.8) brightness(1.2) hue-rotate(240deg)' }}
    />
  </div>

  <div className="relative z-10 mb-4 flex justify-center lg:justify-start">
    <TitleHeader
      badgeText={intelligentAutomation.badgeText}
      titleLines={intelligentAutomation.titleLines}
      wrapperClassName="mb-0 items-center lg:items-start"
      titleClassName="font-rajdhani text-4xl font-semibold text-white leading-tight md:text-4xl lg:text-[2.55rem] lg:leading-[1] lg:[&_p]:whitespace-nowrap xl:text-[2.05rem] xl:leading-[1.05] xl:[&_p]:whitespace-nowrap 2xl:text-[3.4rem] 2xl:[&_p]:whitespace-nowrap min-[2560px]:text-[6.8rem] min-[2560px]:[&_p]:whitespace-nowrap"
      badgeClassName="px-8 py-2 w-full text-xl md:px-10 md:py-3 md:text-2xl lg:px-8 lg:py-2 lg:text-[1.65rem] xl:px-10 xl:py-3 xl:text-[1.8rem] min-[1440px]:px-11 min-[1440px]:text-[1.9rem] 2xl:px-12 2xl:text-[2rem] min-[2560px]:px-16 min-[2560px]:py-5 min-[2560px]:text-[2.5rem]"
      badgeBorderClassName={titleBadgeBorderClassName}
    />
  </div>

  <div className="relative text-center z-10  ml-35 md:ml-60 flex h-[200px] md:h-[300px] w-full max-w-[520px] items-center justify-center transform lg:mx-0 lg:h-[360px] lg:-translate-y-6 xl:h-[420px] 2xl:h-[480px] min-[1440px]:-translate-y-10 min-[2560px]:h-[1150px] min-[2560px]:max-w-[1400px] min-[2560px]:translate-y-20">
    <Lottie animationData={robotAnimation} loop className="w-full h-full scale-[1.9]" />
  </div>

</div>

        {/* RIGHT COLUMN */}
        <div className="relative flex flex-col gap-4 xl:pl-8">
          <div className="relative z-10 flex flex-col gap-4">
            {intelligentAutomation.features.map((feature, index) => {
              const styleIndex = index % 2;
              const cardStyle = CARD_STYLES[styleIndex];
              const isGradientBorder = 'gradientBorder' in cardStyle && cardStyle.gradientBorder;

              return (
                <div
                  key={index}
                  ref={(el) => {cardRefs.current[index] = el}}
                  className="relative w-full overflow-hidden rounded-[25px] p-3 md:p-4 lg:p-4 xl:p-5 2xl:p-6 transition-all duration-700 ease-out hover:scale-[1.05]"
                  style={{
                    backgroundColor: '#0E0924',

                    transform: visible[index] ? 'translateX(0px)' : 'translateX(100px)',
                    opacity: visible[index] ? 1 : 0,

                    ...(isGradientBorder
                      ? {
                          backgroundImage: `linear-gradient(#0E0924, #0E0924), linear-gradient(94.73deg, #EA892A 0%, #E35384 100%)`,
                          backgroundOrigin: 'border-box',
                          backgroundClip: 'padding-box, border-box',
                          border: '1px solid transparent',
                        }
                      : {
                          border: cardStyle.border,
                        }),
                  }}
                >
                  {/* glow */}
                  <div
                    className="absolute"
                    style={{
                      left: '-100px',
                      bottom: '-100px',
                      width: '360px',
                      height: '360px',
                      borderRadius: '9999px',
                      border: `120px solid ${colors.serviceGlowPurple}`,
                      filter: 'blur(140px)',
                    }}
                  />

                  <div
                    className="absolute bg-service-automation-card-blob"
                    style={{
                      right: '-80px',
                      bottom: '-120px',
                      width: '360px',
                      height: '260px',
                      borderRadius: '200px',
                      opacity: 0.14,
                      filter: 'blur(120px)',
                    }}
                  />

                  <div className="relative z-10">
                    <h3 className="mb-2 font-rajdhani font-semibold text-[20px] md:text-[22px] xl:text-[24px] 2xl:text-[28px]" style={cardStyle.headingStyle}>
                      {feature.title}
                    </h3>

                    <p className="text-sm md:text-[13px] xl:text-[14px] 2xl:text-[16px] text-white/70">
                      {feature.description}
                    </p>
                  </div>

                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
};

export default IntelligentAutomationSection;